import { addDays, isAfter, isSameDay, parseISO, startOfDay } from 'date-fns'

import type { ProductCardDataShows } from '../transforms/shows'

/**
 * Sorts shows according to Jira HULY-1942 requirements:
 * 1. Primary: Date & time (soonest first, within next 5 days)
 * 2. Secondary: Ticket price (lowest to highest)
 * 3. Beyond 5 days: Sort strictly by price ascending
 */
export const sortShowsCards = (cards: ProductCardDataShows[]): ProductCardDataShows[] => {
  const now = new Date()
  const todayStart = startOfDay(now)
  const fiveDaysFromNow = addDays(todayStart, 5)

  const withinFiveDays: ProductCardDataShows[] = []
  const beyondFiveDays: ProductCardDataShows[] = []

  // Separate cards into within 5 days and beyond 5 days
  for (const card of cards) {
    try {
      const eventDate = parseISO(card.computed.date)
      const eventDateStart = startOfDay(eventDate)

      // Include shows that are today or in the future, within the next 5 days
      // This ensures shows happening later today are included (even if date is at midnight)
      // Compare dates at start of day to include all shows happening on the same day
      const isTodayOrFuture = isSameDay(eventDateStart, todayStart) || isAfter(eventDateStart, todayStart)
      const isWithinFiveDays = isTodayOrFuture && !isAfter(eventDateStart, fiveDaysFromNow)

      if (isWithinFiveDays) {
        withinFiveDays.push(card)
      } else {
        beyondFiveDays.push(card)
      }
    } catch {
      // If date parsing fails, treat as beyond 5 days
      beyondFiveDays.push(card)
    }
  }

  // Sort within 5 days: by date (soonest first), then by price (lowest first)
  const sortedWithinFiveDays = withinFiveDays.sort((a, b) => {
    try {
      const dateA = parseISO(a.computed.date).getTime()
      const dateB = parseISO(b.computed.date).getTime()

      if (dateA !== dateB) {
        return dateA - dateB
      }

      // If same date, sort by time
      const timeA = a.computed.time || ''
      const timeB = b.computed.time || ''

      if (timeA !== timeB) {
        return timeA.localeCompare(timeB)
      }

      // If same date and time, sort by price
      return a.computed.totalPrice - b.computed.totalPrice
    } catch {
      return 0
    }
  })

  // Sort beyond 5 days: strictly by price ascending
  const sortedBeyondFiveDays = beyondFiveDays.sort((a, b) => {
    return a.computed.totalPrice - b.computed.totalPrice
  })

  // Combine: within 5 days first, then beyond 5 days
  return [...sortedWithinFiveDays, ...sortedBeyondFiveDays]
}
